import { uniq } from 'lodash/fp';
import { FC, useMemo } from 'react';
import styled from 'styled-components';

import JsonSchemaSelect, { Option } from 'dashboard/components/JsonSchemaSelect';
import BaseConditionalInputPossibilities from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/BaseConditionalInputPossibilities';
import {
  ConditionalRuleValueAll,
  ConditionalRuleValueTypes,
  getJsonSchemaType,
  JsonLogicValue,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import { AGE_OPERATORS, WorkflowOperatorKey } from 'dashboard/models/workflow';
import { JsonSchemaAllTypes, JsonSchemaCustomTypes } from 'lib/fields';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';
import { isMultiValue } from 'lib/workflows';

const MinWidthContainer = styled.div`
  min-width: 32px;
`;

/**
 * Determines the list of allowed types for a given fieldSchema and operator
 *
 * @param fieldSchema
 * @param operator
 * @returns
 */
const getAllowedTypes = (
  fieldSchema: JsonSchema,
  operator: WorkflowOperatorKey,
): (JsonSchemaTypes | JsonSchemaCustomTypes)[] => {
  if (!fieldSchema) {
    return [];
  }

  const jsonSchemaType = getJsonSchemaType(fieldSchema);
  const innerTypes = 'items' in fieldSchema ? getAllowedTypes(fieldSchema.items, operator) : [];
  const baseType = AGE_OPERATORS.includes(operator) ? JsonSchemaAllTypes.Number : jsonSchemaType;

  /**
   * Multi value operators exist for both arrays and base types:
   * arrays: my_array CONTAINS value_1, value_2
   * string: my_string IS IN value_1, value_2
   *
   * For array case, include element types as well as array type as options to select
   * We flatten this at runtime so no issue with mixing arrays and base types
   */
  return isMultiValue(operator) ? uniq([baseType, ...innerTypes]) : [baseType];
};

interface SideInputProps {
  ariaLabel?: string;
  id: string;
  configValue: ConditionalRuleValueAll;
  configType: ConditionalRuleValueTypes;
  jsonSchema: JsonSchema;
  disallowedFieldPaths?: string[];
  allowedFieldPaths?: string[];
  fieldSchema?: JsonSchema;
  operator?: WorkflowOperatorKey;
  tokensToDisplayValue: Record<string, string>;
  onFieldSelect: (option: Option) => void;
  setFieldValue?: (value: JsonLogicValue) => void;
  onConfigValueChange?: (val: string) => void;
}

const SideInput: FC<SideInputProps> = ({
  ariaLabel,
  id,
  configValue,
  configType,
  jsonSchema,
  disallowedFieldPaths,
  allowedFieldPaths,
  fieldSchema,
  operator,
  tokensToDisplayValue,
  onFieldSelect,
  setFieldValue,
  onConfigValueChange,
}) => {
  const allowedTypes = useMemo(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => getAllowedTypes(fieldSchema, operator),
    [fieldSchema, operator],
  );

  switch (configType) {
    case RuleValueTypes.Value: {
      return (
        <BaseConditionalInputPossibilities
          operator={operator}
          value={configValue as JsonLogicValue}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          fieldSchema={fieldSchema}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          setFieldValue={setFieldValue}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          isMulti={isMultiValue(operator)}
        />
      );
    }
    case RuleValueTypes.Field: {
      return (
        <MinWidthContainer>
          <JsonSchemaSelect
            ariaLabel={ariaLabel}
            id={id}
            inputValue={configValue as string}
            jsonSchema={jsonSchema}
            allowedTypes={allowedTypes}
            allowedFieldPaths={allowedFieldPaths}
            allowedArrayTypes={[
              JsonSchemaTypes.Boolean,
              JsonSchemaTypes.String,
              JsonSchemaTypes.Number,
              JsonSchemaTypes.Integer,
              JsonSchemaTypes.Object,
            ]}
            disallowedFieldPaths={disallowedFieldPaths}
            tokensToDisplayValue={tokensToDisplayValue}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onFieldSelect={onFieldSelect}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onValueChange={onConfigValueChange}
          />
        </MinWidthContainer>
      );
    }
    default:
      return assertNever(configType);
  }
};

export default SideInput;
